import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from "@headlessui/react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useState } from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";

export interface ComboboxOptionItem {
  id: string | number;
  name: string;
  disabled?: boolean;
}

interface BaseComboboxProps<T extends ComboboxOptionItem> {
  options: T[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  helperText?: string;
  onQueryChange?: (query: string) => void;
  disableLocalFilter?: boolean;
  loading?: boolean;
}

interface SingleComboboxProps<T extends ComboboxOptionItem>
  extends BaseComboboxProps<T> {
  value: T | null;
  onChange: (value: T | null) => void;
}

interface MultiComboboxProps<T extends ComboboxOptionItem>
  extends BaseComboboxProps<T> {
  modal?: boolean;
  value: T[];
  onChange: (value: T[]) => void;
}

const inputStyles =
  "tw-flex-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-outline-none tw-placeholder:text-muted-foreground focus:tw-outline-none focus:tw-ring-0";

const optionStyles = ({
  focus,
  selected,
  disabled,
}: {
  focus: boolean;
  selected: boolean;
  disabled: boolean;
}) =>
  cn(
    "tw-relative tw-flex tw-cursor-default tw-select-none tw-items-center tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm tw-outline-none",
    focus && "tw-bg-accent tw-text-accent-foreground",
    selected && "tw-bg-accent tw-text-accent-foreground",
    disabled && "tw-pointer-events-none tw-opacity-50"
  );

// The options panel is anchored (see `anchor` on ComboboxOptions below), so Headless UI
// renders it in a portal and positions it with floating-ui. That escapes any `overflow:hidden`
// ancestor (accordion/card) that would otherwise clip the dropdown. Width is matched to the
// trigger via the `--input-width` CSS var that the anchored panel exposes.
const dropdownStyles =
  "tw-z-50 tw-max-h-60 tw-w-[var(--input-width)] tw-overflow-auto tw-rounded-md tw-border tw-bg-popover tw-p-1 tw-text-popover-foreground tw-shadow-md tw-empty:tw-hidden";

const dropdownAnchor = { to: "bottom start", gap: 4 } as const;

const emptyMessageStyles =
  "tw-relative tw-cursor-default tw-select-none tw-px-2 tw-py-1.5 tw-text-sm tw-text-muted-foreground";

function DropdownContent<T extends ComboboxOptionItem>({
  loading,
  query,
  options,
  disabledIds = [],
}: {
  loading: boolean;
  query: string;
  options: T[];
  disabledIds?: (string | number)[];
}) {
  if (loading) {
    return (
      <div className={emptyMessageStyles}>{strings("common.loading")}</div>
    );
  }

  if (options.length === 0 && query !== "") {
    return (
      <div className={emptyMessageStyles}>{strings("common.nothingFound")}</div>
    );
  }

  return (
    <>
      {options.map((option) => (
        <ComboboxOption
          key={option.id}
          value={option}
          disabled={option.disabled || disabledIds.includes(option.id)}
          className={optionStyles}
        >
          {({ selected }) => (
            <>
              <span className={cn("tw-flex-1", selected && "tw-font-medium")}>
                {option.name}
              </span>
              {selected && <Check className="tw-h-4 tw-w-4" />}
            </>
          )}
        </ComboboxOption>
      ))}
    </>
  );
}

export function Combobox<T extends ComboboxOptionItem>({
  options,
  value,
  onChange,
  placeholder = strings("placeholders.select"),
  label,
  disabled = false,
  className,
  error,
  helperText,
  onQueryChange,
  disableLocalFilter = false,
  loading = false,
}: SingleComboboxProps<T>) {
  const [query, setQuery] = useState("");

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    onQueryChange?.(newQuery);
  };

  const filteredOptions = disableLocalFilter
    ? options
    : query === ""
      ? options
      : options.filter((option) =>
          option.name.toLowerCase().includes(query.toLowerCase())
        );

  return (
    <div className={cn("tw-w-full", className)}>
      {label && (
        <span className="tw-block tw-text-sm tw-font-medium tw-text-foreground tw-mb-1.5">
          {label}
        </span>
      )}
      <HeadlessCombobox
        value={value}
        onChange={onChange}
        disabled={disabled}
        immediate={true}
      >
        <div className="tw-relative">
          <div
            className={cn(
              "tw-flex tw-min-h-10 tw-w-full tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-ring-offset-background",
              "focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-ring focus-within:tw-ring-offset-2",
              error && "tw-border-destructive",
              disabled && "tw-cursor-not-allowed tw-opacity-50"
            )}
          >
            <ComboboxInput
              className={inputStyles}
              placeholder={placeholder}
              displayValue={(item: T | null) => item?.name || ""}
              onChange={(e) => handleQueryChange(e.target.value)}
            />
            <ComboboxButton className="tw-flex tw-items-center">
              <ChevronsUpDown className="tw-h-4 tw-w-4 tw-opacity-50" />
            </ComboboxButton>
          </div>
          <ComboboxOptions anchor={dropdownAnchor} className={dropdownStyles}>
            <DropdownContent
              loading={loading}
              query={query}
              options={filteredOptions}
            />
          </ComboboxOptions>
        </div>
      </HeadlessCombobox>
      {helperText && (
        <p
          className={cn(
            "tw-mt-1.5 tw-text-xs",
            error ? "tw-text-destructive" : "tw-text-muted-foreground"
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
}

export function MultiCombobox<T extends ComboboxOptionItem>({
  options,
  value,
  onChange,
  placeholder = strings("placeholders.select"),
  label,
  disabled = false,
  className,
  error,
  helperText,
  onQueryChange,
  disableLocalFilter = false,
  loading = false,
  modal = true,
}: MultiComboboxProps<T>) {
  const [query, setQuery] = useState("");

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    onQueryChange?.(newQuery);
  };

  const filteredOptions = disableLocalFilter
    ? options
    : query === ""
      ? options
      : options.filter((option) =>
          option.name.toLowerCase().includes(query.toLowerCase())
        );

  const selectedIds = value.map((v) => v.id);

  // Headless UI does not reset the typed text when an option is picked in `multiple` mode
  // (the panel stays open via `immediate`), so the query lingers in the input and keeps the
  // list filtered to the just-selected item. Clear it ourselves on every selection change.
  const handleSelectionChange = (next: T[]) => {
    onChange(next);
    setQuery("");
  };

  const removeItem = (itemId: string | number) => {
    onChange(value.filter((v) => v.id !== itemId));
  };

  return (
    <div className={cn("tw-w-full", className)}>
      {label && (
        <span className="tw-block tw-text-sm tw-font-medium tw-text-foreground tw-mb-1.5">
          {label}
        </span>
      )}
      <HeadlessCombobox
        value={value}
        onChange={handleSelectionChange}
        disabled={disabled}
        multiple={true}
        immediate={true}
      >
        <div className="tw-relative">
          <div
            className={cn(
              "tw-flex tw-min-h-10 tw-w-full tw-flex-wrap tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-ring-offset-background",
              "focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-ring focus-within:tw-ring-offset-2",
              error && "tw-border-destructive",
              disabled && "tw-cursor-not-allowed tw-opacity-50"
            )}
          >
            {value.map((item) => (
              <span
                key={item.id}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-bg-secondary tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium"
              >
                {item.name}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeItem(item.id);
                  }}
                  className="tw-rounded-sm hover:tw-bg-secondary-foreground/20"
                >
                  <X className="tw-h-3 tw-w-3" />
                </button>
              </span>
            ))}
            <ComboboxInput
              className={cn(inputStyles, "tw-min-w-[120px]")}
              placeholder={value.length > 0 ? "" : placeholder}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
            />
            <ComboboxButton className="tw-flex tw-items-center">
              <ChevronsUpDown className="tw-h-4 tw-w-4 tw-opacity-50" />
            </ComboboxButton>
          </div>
          <ComboboxOptions
            anchor={dropdownAnchor}
            className={dropdownStyles}
            modal={modal}
          >
            <DropdownContent
              loading={loading}
              query={query}
              options={filteredOptions}
              disabledIds={selectedIds}
            />
          </ComboboxOptions>
        </div>
      </HeadlessCombobox>
      {helperText && (
        <p
          className={cn(
            "tw-mt-1.5 tw-text-xs",
            error ? "tw-text-destructive" : "tw-text-muted-foreground"
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
}
